import { Col, Divider, Form, Row, Table, TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';

// components
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import BoardMembers from './boardMembers';

// services
import { isRTL } from 'core/services/language';

// styles
import styles from '../boards.module.scss';

// models
import { BoardMember } from '../models/members';
import { Board } from '../models/board';
import { Meeting } from '../models/meetings';
import { StatusColors } from 'utils/statusColors';
import StatusComponent from 'components/status/status';
import { onGetMeetingsList } from '../services/meetingServices';
import { dateFormat } from 'utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import TableTotal from 'components/tableTotal/tableTotal';
import { onGetMembersList } from '../services/memberServices';
import BoardSettingsComponent from './boardSettings';

interface BoardItemProps {
	board: Board;
	refresh?(): void;
}

const BoardItem: FunctionComponent<BoardItemProps> = ({ board, refresh }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [boardForm] = Form.useForm();

	const [meetingsLoading, setMeetingsLoading] = useState<boolean>(true);
	const [currentMeetingPage, setCurrentMeetingPage] = useState<number>(1);
	const [meetings, setMeetings] = useState<Meeting[]>([]);
	const [totalMeetingCount, setTotalMeetingCount] = useState<number>(0);

	const [membersLoading, setMembersLoading] = useState<boolean>(true);
	const [members, setMembers] = useState<BoardMember[]>([]);

	const meetingsColumns: TableProps<Meeting>['columns'] = [
		{
			title: t('ACTIONS.CODE'),
			dataIndex: 'meeting_number',
			key: 'meeting_number',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status?.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('ACTIONS.MEETING_DATE'),
			dataIndex: 'date',
			key: 'date',
			render: (date) => dateFormat(date),
		},
		{
			title: t('ACTIONS.TYPE'),
			dataIndex: 'translated_type',
			key: 'translated_type',
			render: (translated_type) => translated_type[isRTL() ? 'ar' : 'en'],
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '_',
			key: '_',
			render: (_, row) => <ListButton type="view" onClick={() => navigate(`/dashboard/actions/boards/meetings/view/${row.id}`)} />,
		},
	];

	useEffect(() => {
		getBoardMeetings();
		getBoardMembers();
	}, []);

	const getBoardMeetings = async () => {
		try {
			setMeetingsLoading(true);

			const response = await onGetMeetingsList(board.id, 1);
			setMeetings(response.meetings);
			setTotalMeetingCount(response.pagination.total);

			setMeetingsLoading(false);
		} catch (error) {
			setMeetingsLoading(false);
		}
	};

	const fetchMoreMeetingData = async (page: number) => {
		try {
			setMeetingsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetMeetingsList(currentMeetingPage);
			setMeetings(response.meetings);

			setCurrentMeetingPage(page);
			setMeetingsLoading(false);
		} catch (error) {
			setMeetingsLoading(false);
		}
	};

	const getBoardMembers = async () => {
		try {
			setMembersLoading(true);

			const response = await onGetMembersList(board.id.toString(), 1);
			setMembers(response.members);

			setMembersLoading(false);
		} catch (error) {
			setMembersLoading(false);
		}
	};

	return (
		<div className={`${styles.boardItem}`}>
			<Row gutter={[16, 16]}>
				<Col span={24}>
					<Form form={boardForm} initialValues={board}>
						<BoardSettingsComponent form={boardForm} viewOnly />
					</Form>
				</Col>

				<Col span={24}>
					<Divider className="my-2" />
				</Col>

				<Col span={24} className={board.status === 'draft' ? 'mb-4' : ''}>
					<h6 className="text-primary mb-4">{t('ACTIONS.BOARD_MEMBERS')}</h6>
					<BoardMembers
						members={members}
						boardStatus={board.status}
						viewOnly
						isLoading={membersLoading}
						canUpdateSecretary={board.allow_secretary_update_while_active}
						boardId={board.id}
						refresh={refresh}
					/>
				</Col>

				{board.status !== 'draft' && (
					<>
						<Col span={24}>
							<Divider className="mb-2" />
						</Col>

						<Col span={24}>
							<h6 className="text-primary mb-4">{t('ACTIONS.BOARD_MEETINGS')}</h6>
							<Table
								columns={meetingsColumns}
								dataSource={meetings}
								className="bordered mb-4"
								scroll={{ x: 600 }}
								rowKey="id"
								loading={meetingsLoading}
								pagination={{
									pageSize: 15,
									total: totalMeetingCount,
									showSizeChanger: false,
									current: currentMeetingPage,
									onChange: fetchMoreMeetingData,
									showTotal: (total) => <TableTotal total={total} />,
								}}
								locale={{ emptyText: <NoData /> }}
								sticky
							/>
						</Col>
					</>
				)}
			</Row>
		</div>
	);
};

export default BoardItem;
